import { z } from "zod";
import { addVat, type EstimateResult } from "./pricing/computeEstimate.js";

/** ข้อมูลบริษัทผู้ออกใบเสนอราคา (เก็บต่อบัญชี ใช้ซ้ำทุกใบ) */
const digits13 = z.string().regex(/^\d{13}$/, "tax_id_format");

/** ตรวจเลขผู้เสียภาษี/บัตรประชาชน 13 หลักด้วย check digit (อัลกอริทึมกรมสรรพากร) */
export function isValidThaiTaxId(id: string): boolean {
  if (!/^\d{13}$/.test(id)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(id[i]) * (13 - i);
  return (11 - (sum % 11)) % 10 === Number(id[12]);
}

export const companyProfileSchema = z.object({
  name: z.string().trim().min(1).max(160),
  address: z.string().trim().min(1).max(400),
  taxId: digits13.refine(isValidThaiTaxId, "tax_id_checksum"),
  phone: z.string().trim().max(30).optional(),
  email: z.string().trim().email().max(120).optional().or(z.literal("")),
  signerName: z.string().trim().max(120).optional(),
  signerTitle: z.string().trim().max(120).optional(),
});
export type CompanyProfile = z.infer<typeof companyProfileSchema>;

export const LOGO_TYPES = ["image/png", "image/jpeg"] as const;
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/** GET/PUT /company */
export interface CompanyResponse {
  company: (CompanyProfile & { logoKey?: string }) | null;
  logoUrl?: string;
}
/** POST /company/logo/presign {contentType,size} -> {key,url,headers,expiresIn} ; แล้ว PUT ไฟล์ ; POST /company/logo/verify {key} */
export const logoPresignSchema = z.object({ contentType: z.enum(LOGO_TYPES), size: z.number().int().positive().max(MAX_LOGO_BYTES) });
export const logoVerifySchema = z.object({ key: z.string().min(1).max(200) });

export const VALIDITY_STEP_DAYS = 15;
export const createQuotationSchema = z.object({
  customer: z.object({
    name: z.string().trim().min(1).max(160),
    phone: z.string().trim().max(30).optional(),
    address: z.string().trim().max(400).optional(),
    taxId: z.string().trim().max(13).optional(),
  }),
  /** ยืนราคา (วัน) ทีละ 15 วัน */
  validityDays: z.number().int().min(15).max(120).refine((n) => n % VALIDITY_STEP_DAYS === 0, "validity_step"),
  paymentTerms: z.string().trim().min(1).max(600),
  notes: z.string().trim().max(600).optional(),
  includeSignature: z.boolean().default(true),
  /** บวก VAT 7% ในใบเสนอราคา (ค่าตั้งต้นเปิด ปิดได้ถ้ากิจการไม่ได้จดทะเบียน VAT) */
  vat: z.boolean().default(true),
});
export type CreateQuotationRequest = z.infer<typeof createQuotationSchema>;

export interface QuotationLine {
  no: number;
  description_th: string;
  qty: number;
  unit: string;
  unitPrice: number | null;
  amount: number;
}
export interface QuotationTotals {
  subtotal: number;
  overhead: number;
  discount: number;
  beforeVat: number;
  vat: number;
  grand: number;
  vatEnabled: boolean;
  overheadPct: number;
}
export interface QuotationSummary {
  quotationId: string;
  number: string;
  jobId: string;
  issuedAt: string;
  validUntil: string;
  grandTotal: number;
}
export interface QuotationDto extends QuotationSummary {
  company: CompanyProfile;
  customer: CreateQuotationRequest["customer"];
  lines: QuotationLine[];
  totals: QuotationTotals;
  amountThaiText: string;
  paymentTerms: string;
  notes?: string;
  includeSignature: boolean;
  /** ลิงก์ดาวน์โหลด PDF (presigned อายุสั้น) */
  pdfUrl: string;
}

/** เลขที่ใบเสนอราคา QT-YYYYMM-0001 (ปี ค.ศ. running ต่อบัญชีต่อเดือน) */
export function formatQuotationNumber(yyyymm: string, seq: number): string {
  return `QT-${yyyymm}-${String(seq).padStart(4, "0")}`;
}

/** สร้างรายการในใบเสนอราคาจากผลคำนวณ (user_value): ค่าวัสดุ ค่าแรง และรายการเพิ่มเอง ต่อพื้นที่ทำงาน */
export function quotationLinesFromResult(result: EstimateResult, labels: (string | undefined)[] = []): QuotationLine[] {
  const lines: Omit<QuotationLine, "no">[] = [];
  result.groups.forEach((g, i) => {
    const label = g.label_th ?? labels[i] ?? g.category_id;
    const unit = unitLabelTh(g.unit);
    if (g.materials.baseTotal > 0) {
      if (g.materials.mode === "reference") {
        lines.push({ description_th: `ค่าวัสดุ ${label}`, qty: g.workQty, unit, unitPrice: g.materials.rate, amount: g.materials.baseTotal });
      } else {
        for (const l of g.materials.lines.filter((l) => !l.custom && l.total !== null && l.unitPrice !== null)) lines.push({ description_th: `${l.name_th} (${label})`, qty: l.qty, unit: l.unit, unitPrice: l.unitPrice, amount: l.total! });
      }
    }
    for (const l of g.materials.lines.filter((l) => l.custom && l.total !== null)) lines.push({ description_th: l.name_th, qty: l.qty, unit: l.unit, unitPrice: l.unitPrice, amount: l.total! });
    if (g.labor.total > 0) lines.push({ description_th: `ค่าแรง ${label}`, qty: g.workQty, unit, unitPrice: g.labor.rate, amount: g.labor.total });
  });
  return lines.map((l, i) => ({ no: i + 1, ...l }));
}

export function quotationTotalsFromResult(result: EstimateResult, overheadPct: number, vatEnabled: boolean): QuotationTotals {
  const t = result.totals;
  // VAT คำนวณจากราคาก่อน VAT ตามที่เลือกในใบเสนอราคา ไม่ขึ้นกับค่า VAT ที่งานประเมินบันทึกไว้
  const { vat, grand } = addVat(t.beforeVat, vatEnabled);
  return { subtotal: t.subtotal, overhead: t.overhead, discount: t.discount, beforeVat: t.beforeVat, vat, grand, vatEnabled, overheadPct };
}

const UNIT_TH: Record<string, string> = { sqm: "ตร.ม.", point: "จุด", unit: "หน่วย", kg: "กก.", cbm: "ลบ.ม." };
export const unitLabelTh = (u: string): string => UNIT_TH[u] ?? u;

const DIGITS = ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า"];
const PLACES = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน"];

function readBelowMillion(n: number, hasHigher = false): string {
  const s = String(n);
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const d = Number(s[i]);
    const pos = s.length - i - 1;
    if (d === 0) continue;
    if (pos === 1 && d === 1) out += "สิบ";
    else if (pos === 1 && d === 2) out += "ยี่สิบ";
    else if (pos === 0 && d === 1 && (s.length > 1 || hasHigher)) out += "เอ็ด";
    else out += DIGITS[d]! + PLACES[pos]!;
  }
  return out;
}

function readInteger(n: number): string {
  if (n === 0) return "ศูนย์";
  const millions = Math.floor(n / 1_000_000);
  const rest = n % 1_000_000;
  return (millions > 0 ? readInteger(millions) + "ล้าน" : "") + (rest > 0 ? readBelowMillion(rest, millions > 0) : "");
}

/** จำนวนเงินเป็นตัวอักษรไทย เช่น 12,345.50 -> หนึ่งหมื่นสองพันสามร้อยสี่สิบห้าบาทห้าสิบสตางค์ ; ไม่มีสตางค์ลงท้าย "ถ้วน" */
export function thaiBahtText(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) throw new RangeError("amount must be a non-negative number");
  const satangTotal = Math.round((amount + Number.EPSILON) * 100);
  const baht = Math.floor(satangTotal / 100);
  const satang = satangTotal % 100;
  const bahtText = readInteger(baht) + "บาท";
  return satang === 0 ? bahtText + "ถ้วน" : bahtText + readBelowMillion(satang) + "สตางค์";
}

/** ใช้ลิงก์แชร์แบบอ่านอย่างเดียว: GET /public/share/{token} (ไม่ต้องล็อกอิน ไม่มีข้อมูลติดต่อลูกค้า) */
export interface ShareView {
  title: string;
  companyName?: string;
  issuedAt: string;
  expiresAt: string;
  lines: QuotationLine[];
  totals: QuotationTotals;
  areas: { label_th: string; summary: string }[];
  assumptions_th: string[];
  risks_th: string[];
  sources: { name: string; kind_label_th: string; effective_from: string }[];
  photos: { url: string }[];
  disclaimer_th: string;
}
export interface ShareLinkDto {
  token: string;
  url: string;
  createdAt: string;
  expiresAt: string;
}
export const createShareSchema = z.object({ days: z.number().int().min(1).max(30).default(7) });

/** เวอร์ชันตั้งต้นของข้อความยินยอม PDPA/ความเป็นส่วนตัว (ใช้เมื่อ Admin ยังไม่ได้แก้นโยบาย) เวอร์ชันจริงมาจาก GET /policy และ MeResponse.requiredConsentVersion */
export const CURRENT_CONSENT_VERSION = "2026-10";
export const acceptConsentSchema = z.object({ version: z.string().min(1).max(40) });
export const deleteAccountSchema = z.object({ confirm: z.literal("DELETE") });
